import { api, getAccessToken, type QueryParams } from "@/lib/api/client";

export interface OvertimeDay {
  id: string;
  user_id: string;
  employee_name: string | null;
  employee_number: string | null;
  work_date: string;
  first_punch_at: string | null;
  last_punch_at: string | null;
  punch_count: number;
  span_minutes: number;
  day_kind: "working" | "weekly_off" | "public_holiday";
  scheduled_minutes: number;
  computed_minutes: number;
  adjusted_minutes: number | null;
  effective_minutes: number;
  needs_review: boolean;
}

export interface OvertimeBalance {
  user_id: string;
  employee_name: string | null;
  employee_number: string | null;
  balance_minutes: number;
  balance_hours: string;
}

/**
 * A month added up, every figure a plain sum or count over its rows.
 *
 * Overtime is `effective_minutes`; `computed_minutes` is what the punches
 * said before HR adjusted anything, and `adjustment_minutes` the difference.
 */
export interface AttendanceMonthTotals {
  days_recorded: number;
  working_days: number;
  weekly_off_days: number;
  public_holidays: number;
  punch_count: number;
  span_minutes: number;
  scheduled_minutes: number;
  computed_minutes: number;
  effective_minutes: number;
  adjustment_minutes: number;
  adjusted_days: number;
  days_with_overtime: number;
  days_needing_review: number;
  /**
   * From the day-by-day review, over the days HR has sent. Short is the
   * minutes of working days not worked and not covered by leave; an
   * unexplained absence counts as its whole day. Absent from an older server.
   */
  short_minutes?: number;
  absent_days?: number;
  late_days?: number;
  late_minutes?: number;
  leave_days?: number;
  overtime_used_minutes?: number;
  missing_punch_days?: number;
}

/** One month of the signed-in person's own attendance. */
export interface AttendanceMonth {
  month: string;
  month_start: string;
  month_end: string;
  employee_name: string | null;
  employee_number: string | null;
  hours_per_day: string | null;
  days: OvertimeDay[];
  totals: AttendanceMonthTotals;
  /** Every month they have a record in, newest first. */
  months: string[];
  produced_at: string;
  /** Each day HR has sent, punches or none, with what happened on it. */
  lines?: AttendanceLine[];
  /** HR is still checking some of the month; those days are not shown yet. */
  being_checked?: boolean;
  /** Their own requests for a correction in the month, newest first. */
  rectifications?: Rectification[];
}

/** What a person's day was, as HR checks it and the employee later reads it. */
export type LineStatus =
  | "present"
  | "missing_punch"
  | "on_leave"
  | "rest_day"
  | "public_holiday"
  | "absent";

export type LineFlag =
  | "missing_punch"
  | "long_span"
  | "absent"
  | "late"
  | "overtime_earned"
  | "overtime_used"
  | "short"
  | "leave"
  | "rectification"
  | "corrected"
  | "accepted";

export type RectificationStatus = "pending" | "approved" | "rejected" | "withdrawn";

/** An employee saying a day is wrong, and HR's answer. */
export interface Rectification {
  id: string;
  user_id: string;
  employee_name: string | null;
  employee_number: string | null;
  work_date: string;
  message: string;
  /** "HH:MM:SS", Dubai wall-clock. */
  proposed_in: string | null;
  proposed_out: string | null;
  status: RectificationStatus;
  decided_at: string | null;
  decided_by_name: string | null;
  decision_note: string | null;
  created_at: string;
  /** The day as it stands now, for HR to compare with the claim. */
  current_in_at: string | null;
  current_out_at: string | null;
  current_status: LineStatus | null;
  current_overtime_minutes: number | null;
}

/** One person on one date. */
export interface AttendanceLine {
  user_id: string;
  employee_name: string;
  employee_number: string | null;
  work_date: string;
  status: LineStatus;
  day_kind: OvertimeDay["day_kind"];
  flags: LineFlag[];
  /** Something here needs a decision before the day goes out. */
  attention: boolean;
  in_at: string | null;
  out_at: string | null;
  punch_count: number;
  worked_minutes: number;
  scheduled_minutes: number;
  overtime_minutes: number;
  computed_overtime_minutes: number;
  short_minutes: number;
  late_minutes: number;
  /** "HH:MM:SS" when the rota or the position says when they were due. */
  due_at: string | null;
  overtime_used_minutes: number;
  leave_type: string | null;
  leave_part: "full" | "half_day" | "hours" | null;
  overtime_day_id: string | null;
  corrected_in: boolean;
  corrected_out: boolean;
  overtime_adjusted: boolean;
  short_adjusted: boolean;
  accepted: boolean;
  review_note: string | null;
  rectification: Rectification | null;
}

export type ReviewState = "open" | "confirmed" | "sent";

export interface ReviewDayCounts {
  employees: number;
  present: number;
  on_leave: number;
  off: number;
  absent: number;
  missing_punch: number;
  late: number;
  overtime_earned: number;
  overtime_used: number;
  short: number;
  rectifications: number;
  attention: number;
}

export interface ReviewDaySummary {
  work_date: string;
  state: ReviewState;
  confirmed_at: string | null;
  confirmed_by_name: string | null;
  published_at: string | null;
  counts: ReviewDayCounts;
}

export interface ReviewRange {
  from_date: string;
  to_date: string;
  days: ReviewDaySummary[];
  all_confirmed: boolean;
  all_sent: boolean;
  latest_import: ImportSummary | null;
}

export interface ReviewDay {
  summary: ReviewDaySummary;
  lines: AttendanceLine[];
}

/**
 * HR's correction to one day. A field left out is kept as it is; one sent as
 * null clears what HR set before. Times are "HH:MM" in Dubai.
 */
export interface LineCorrection {
  in_time?: string | null;
  out_time?: string | null;
  overtime_minutes?: number | null;
  short_minutes?: number | null;
  accept?: boolean | null;
  reason: string;
}

export interface RectificationRequest {
  work_date: string;
  message: string;
  proposed_in?: string | null;
  proposed_out?: string | null;
}

export type RectificationApproval = Omit<LineCorrection, "reason"> & { note?: string | null };

export interface ReviewPeriod {
  from_date: string;
  to_date: string;
}

export type AttendanceFileKind = "csv" | "pdf";

export interface ImportSummary {
  id: string;
  status: "processing" | "completed" | "failed";
  row_count: number;
  punches_new: number;
  punches_duplicate: number;
  rows_rejected: number;
  rejected_rows: { line: number; reason: string; row: string[] }[] | null;
  unmatched_badges: number;
  date_min: string | null;
  date_max: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface UnmatchedBadge {
  badge_number: string;
  employee_name: string | null;
  department: string | null;
  punch_count: number;
  first_seen: string;
  last_seen: string;
}

export interface LedgerEntry {
  id: string;
  user_id: string;
  entry_date: string;
  kind: string;
  minutes: number;
  reason: string | null;
  leave_request_id: string | null;
  created_at: string;
}

export const overtimeKeys = {
  all: ["overtime"] as const,
  days: (filters: QueryParams) => [...overtimeKeys.all, "days", filters] as const,
  myDays: () => [...overtimeKeys.all, "my-days"] as const,
  myAttendance: (month: string) => [...overtimeKeys.all, "my-attendance", month] as const,
  myRectifications: () => [...overtimeKeys.all, "my-rectifications"] as const,
  review: (period: Partial<ReviewPeriod>) => [...overtimeKeys.all, "review", period] as const,
  reviewDay: (date: string) => [...overtimeKeys.all, "review-day", date] as const,
  rectifications: (status: string) => [...overtimeKeys.all, "rectifications", status] as const,
  myBalance: () => [...overtimeKeys.all, "my-balance"] as const,
  balances: () => [...overtimeKeys.all, "balances"] as const,
  imports: () => [...overtimeKeys.all, "imports"] as const,
  unmatched: () => [...overtimeKeys.all, "unmatched"] as const,
  ledger: (userId?: string) => [...overtimeKeys.all, "ledger", userId ?? "all"] as const,
};

export const overtimeApi = {
  days: (filters: QueryParams) => api.get<OvertimeDay[]>("/overtime/days", { query: filters }),
  myDays: (filters: QueryParams = {}) =>
    api.get<OvertimeDay[]>("/me/overtime/days", { query: filters }),
  myAttendance: (month: string) =>
    api.get<AttendanceMonth>("/me/attendance", { query: { month } }),
  myBalance: () => api.get<OvertimeBalance>("/me/overtime/balance"),
  balances: () => api.get<OvertimeBalance[]>("/overtime/balances"),

  adjust: (dayId: string, minutes: number, reason: string) =>
    api.post<OvertimeDay>(`/overtime/days/${dayId}/adjust`, { minutes, reason }),

  clearAdjustment: (dayId: string) => api.delete<OvertimeDay>(`/overtime/days/${dayId}/adjust`),

  recompute: (payload: { user_id?: string; from_date: string; to_date: string }) =>
    api.post<{ days_recomputed: number }>("/overtime/recompute", payload),

  ledger: (userId?: string) =>
    api.get<LedgerEntry[]>("/overtime/ledger", { query: userId ? { user_id: userId } : undefined }),

  addLedgerEntry: (payload: { user_id: string; minutes: number; reason: string }) =>
    api.post<LedgerEntry>("/overtime/ledger", payload),

  imports: () => api.get<ImportSummary[]>("/attendance/imports"),

  importFile: (file: File) => {
    const body = new FormData();
    body.append("file", file);
    return api.post<ImportSummary>("/attendance/imports", body);
  },

  unmatchedBadges: () => api.get<UnmatchedBadge[]>("/attendance/unmatched-badges"),

  mapBadge: (badge_number: string, user_id: string) =>
    api.post<{ message: string }>("/attendance/badge-mappings", { badge_number, user_id }),

  // ── HR checking the days ──
  review: (period: Partial<ReviewPeriod>) =>
    api.get<ReviewRange>("/attendance/review", {
      query: period.from_date && period.to_date ? { from: period.from_date, to: period.to_date } : {},
    }),
  reviewDay: (date: string) => api.get<ReviewDay>(`/attendance/review/days/${date}`),
  confirmDay: (date: string) =>
    api.post<ReviewDaySummary>(`/attendance/review/days/${date}/confirm`),
  reopenDay: (date: string) =>
    api.delete<ReviewDaySummary>(`/attendance/review/days/${date}/confirm`),
  confirmPeriod: (period: ReviewPeriod & { only_clean: boolean }) =>
    api.post<{ confirmed: string[]; skipped: string[] }>("/attendance/review/confirm", period),
  sendPeriod: (period: ReviewPeriod) =>
    api.post<{ days_sent: number; employees_notified: number }>("/attendance/review/send", period),
  correctLine: (date: string, userId: string, correction: LineCorrection) =>
    api.post<AttendanceLine>(
      `/attendance/review/days/${date}/employees/${userId}/correct`,
      correction,
    ),

  // ── Corrections employees asked for ──
  rectifications: (status: string) =>
    api.get<Rectification[]>("/attendance/rectifications", { query: { status } }),
  approveRectification: (id: string, approval: RectificationApproval) =>
    api.post<Rectification>(`/attendance/rectifications/${id}/approve`, approval),
  rejectRectification: (id: string, reason: string) =>
    api.post<Rectification>(`/attendance/rectifications/${id}/reject`, { reason }),
  myRectifications: () => api.get<Rectification[]>("/me/attendance/rectifications"),
  requestRectification: (request: RectificationRequest) =>
    api.post<Rectification>("/me/attendance/rectifications", request),
  withdrawRectification: (id: string) =>
    api.post<Rectification>(`/me/attendance/rectifications/${id}/withdraw`),
};

/** The name the server gave a file, read off its Content-Disposition. */
export function fileNameFrom(disposition: string | null): string | null {
  const match = /filename="([^"]+)"/.exec(disposition ?? "");
  return match?.[1] ?? null;
}

/**
 * Save a month of the signed-in person's own attendance as a file.
 *
 * Fetched with the bearer token and handed to the browser as a blob, because
 * the file sits behind the same authorisation as everything else and a bare
 * link carries no token. The server names the file after the employee number
 * and the month; that name is kept when it is given.
 */
export async function downloadMyAttendance(
  month: string,
  kind: AttendanceFileKind,
): Promise<boolean> {
  const response = await fetch(
    `/api/v1/me/attendance.${kind}?month=${encodeURIComponent(month)}`,
    {
      headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
      credentials: "include",
    },
  );
  if (!response.ok) return false;

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download =
    fileNameFrom(response.headers.get("content-disposition")) ?? `attendance-${month}.${kind}`;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}
